import { Drawer } from 'antd'
import styled from 'styled-components'
import { Text, Button, Heading } from '@lmc/ui-kit/src/general'
import { Spinner, Edit } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import StockCount from '../../iconsV2/StockCount'
import Delivery from '../../iconsV2/Delivery'
import Disposal from '../../iconsV2/Disposal'
import Administration from '../../iconsV2/Administration'
import Comment from '../../iconsV2/Comment'
import { Tag } from 'antd/lib'
import { StockCheckDetailContent } from './StockCheckDetailContent'
import { AdministrationDetailContent } from './AdministrationDetailContent'
import { CommentDetailContent } from './CommentDetailContent'
import { DeliveryDetailContent } from './DeliveryDetailContent'
import { DisposalDetailContent } from './DisposalDetailContent'
import { AdditionalDetailsSection } from './AdditionalDetailsSection'

type ActionType =
  | 'stock-count'
  | 'disposal'
  | 'delivery'
  | 'administration'
  | 'comment'

type StockRecordDrawerProps = {
  isVisible: boolean
  onClose: () => void
  record: any
  recordType?: 'view' | 'edit' | 'administration' | 'comment'
  medicationName: string
}

const renderActionIcon = (type: ActionType) => {
  switch (type) {
    case 'stock-count':
      return <StockCount />
    case 'disposal':
      return <Disposal />
    case 'delivery':
      return <Delivery />
    case 'administration':
      return <Administration />
    case 'comment':
      return <Comment />
    default:
      return null
  }
}

const getTitleText = (actionType: ActionType, t: (key: string) => string) => {
  switch (actionType) {
    case 'stock-count':
      return t('STOCK_CHECK')
    case 'delivery':
      return t('DELIVERY')
    case 'disposal':
      return t('DISPOSAL')
    case 'administration':
      return t('ADMINISTERED')
    case 'comment':
      return t('COMMENT')
    default:
      return ''
  }
}

export const StockRecordDrawer = ({
  isVisible,
  onClose,
  record,
  recordType = 'view',
  medicationName
}: StockRecordDrawerProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const isLoading = false

  const actionType = record?.actionType as ActionType | undefined

  const handleEdit = () => {
    console.log('Edit clicked for record:', record?.id)
    // TODO: Open edit modal/drawer
  }

  if (!actionType || !record) return null

  const titleText = getTitleText(actionType, t)
  const icon = renderActionIcon(actionType)

  const showEditButton =
    recordType === 'view' &&
    actionType !== 'administration' &&
    actionType !== 'comment'

  const showDetailsSection = true // Always show Details section

  const renderContent = () => {
    switch (actionType) {
      case 'stock-count':
        return (
          <StockCheckDetailContent
            record={record}
            medicationName={medicationName}
          />
        )
      case 'administration':
        return (
          <AdministrationDetailContent
            record={record}
            medicationName={medicationName}
          />
        )
      case 'comment':
        return (
          <CommentDetailContent
            record={record}
            medicationName={medicationName}
          />
        )
      case 'delivery':
        return (
          <DeliveryDetailContent
            record={record}
            medicationName={medicationName}
          />
        )
      case 'disposal':
        return (
          <DisposalDetailContent
            record={record}
            medicationName={medicationName}
          />
        )
      default:
        return <div>Detail component - coming soon</div>
    }
  }

  return (
    <StyledDrawer
      title={
        <>
          {isLoading && (
            <LoadingWrapper>
              <Spinner />
            </LoadingWrapper>
          )}
          {!isLoading && record && (
            <TitleWrapper>
              <MedicationNameRow>
                <MedicationNameText>{medicationName}</MedicationNameText>
                <Tag>CD</Tag>
              </MedicationNameRow>
              <TitleContainer>
                {icon && <IconWrapper>{icon}</IconWrapper>}
                <StyledTitleHeading level={4}>{titleText}</StyledTitleHeading>
              </TitleContainer>
            </TitleWrapper>
          )}
        </>
      }
      placement="right"
      onClose={onClose}
      open={isVisible}
      width={736}
    >
      {isLoading && (
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      )}
      {!isLoading && record && (
        <DrawerContent>
          {showDetailsSection && (
            <TopContent>
              <DetailsHeading level={3}>Details</DetailsHeading>
              {showEditButton && (
                <Button variant="text" onClick={handleEdit}>
                  <Edit />
                  {t('EDIT')}
                </Button>
              )}
            </TopContent>
          )}
          {renderContent()}
          <AdditionalDetailsSection record={record} />
        </DrawerContent>
      )}
    </StyledDrawer>
  )
}

const StyledDrawer = styled(Drawer)`
  .ant-drawer-header {
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
  }

  .ant-drawer-header-title {
    flex-direction: row-reverse;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`

const StyledTitleHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`

const DrawerContent = styled.div``

const MedicationNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MedicationNameText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const DetailsHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const TopContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 0 16px 0;
`
